// React context that holds the signed-in user (if any) plus methods
// for signin / signup / signout. The provider also lazily resolves
// the current token at mount time by calling `/me`, so a tab that
// already has a stored token comes up signed in without the user
// hitting a button.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { ApiError, api, type User } from "./client";
import { licenseStore } from "./license";
import { tokenStore } from "./token";

// Anyone who's signed in counts as past the activation gate, even
// if their browser doesn't have a license code in localStorage —
// that's the case after signing in from a new device. We persist a
// stable sentinel (the user's license_code, falling back to a
// labelled placeholder for legacy users with no code on record) so
// the next reload short-circuits the gate without re-running the
// /me probe.
function rehydrateLicense(user: User): void {
  if (user.license_code) {
    licenseStore.set(user.license_code);
  } else if (licenseStore.get() === null) {
    // Legacy / pre-licensing user. Park a sentinel so the gate
    // doesn't relock them on the next reload.
    licenseStore.set("VERIFIED-VIA-SIGNIN");
  }
}

export interface AuthState {
  /** The authenticated user, or null if the visitor isn't signed in. */
  user: User | null;
  /** True until the initial /me lookup resolves on mount. */
  loading: boolean;
  signup: (email: string, password: string, licenseCode: string) => Promise<void>;
  signin: (email: string, password: string) => Promise<void>;
  signout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Resolve "do I already have a valid session?" on mount. If /me
  // returns 401 we discard the stored token; anything else (network
  // failure, server down) is treated as transient and we keep the
  // token so the next mount can retry.
  useEffect(() => {
    let cancelled = false;
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return () => {};
    }
    api
      .me()
      .then(({ user }) => {
        if (!cancelled) {
          rehydrateLicense(user);
          setUser(user);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          tokenStore.set(null);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signup: async (email, password, licenseCode) => {
        const { token, user } = await api.signup(email, password, licenseCode);
        tokenStore.set(token);
        rehydrateLicense(user);
        setUser(user);
      },
      signin: async (email, password) => {
        const { token, user } = await api.signin(email, password);
        tokenStore.set(token);
        rehydrateLicense(user);
        setUser(user);
      },
      signout: () => {
        // Sign-out clears the JWT but leaves the license sentinel
        // in place — the user has already proven they're allowed
        // here, and we don't want a stray "log out" click to drop
        // them back to the activation gate. The gate is a "first
        // time on this browser" wall, not a per-session check.
        tokenStore.set(null);
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
